import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useSiteLanguage } from "@/lib/useSiteLanguage";
import { techCopy, techExtraCopy, techStackGroupCopy } from "@/lib/siteCopy";

type TechIcon = {
  name: string;
  src: string;
  /** Absolute placement - kept outside the center copy safe zone */
  position: string;
  size: string;
  duration: string;
  delay: string;
};

type StackGroup = {
  label: string;
  blurb: string;
  items: string[];
  icons: string[];
};

/**
 * Constellation around the centered copy.
 * Uses every unique asset in /public/tech (prefer .png over .jpg duplicates).
 * Safe zone (no icons): ~30–70% X and ~22–78% Y.
 */
const techIcons: TechIcon[] = [
  // -- Top arc --
  {
    name: "JavaScript",
    src: "/tech/javascript.png",
    position: "left-1/2 top-[1.5%] -translate-x-1/2",
    size: "size-14 lg:size-[4.25rem]",
    duration: "7s",
    delay: "0.2s",
  },
  {
    name: "VS Code",
    src: "/tech/Visual%20Studio%20Code%20(VS%20Code).png",
    position: "left-[38%] top-[3%]",
    size: "size-8 lg:size-9",
    duration: "6.4s",
    delay: "1.1s",
  },
  {
    name: "Vite",
    src: "/tech/Vite.png",
    position: "right-[38%] top-[3%]",
    size: "size-8 lg:size-9",
    duration: "5.5s",
    delay: "1.3s",
  },
  {
    name: "Tailwind CSS",
    src: "/tech/Tailwind%20CSS.png",
    position: "left-[26%] top-[5%]",
    size: "size-8 lg:size-9",
    duration: "7.4s",
    delay: "0.8s",
  },
  {
    name: "Flutter",
    src: "/tech/Flutter.png",
    position: "right-[26%] top-[5%]",
    size: "size-8 lg:size-9",
    duration: "6.3s",
    delay: "1.6s",
  },
  {
    name: "GitHub",
    src: "/tech/GitHub.png",
    position: "left-[14%] top-[4%]",
    size: "size-8 lg:size-9",
    duration: "6.8s",
    delay: "0.4s",
  },
  {
    name: "GitLab",
    src: "/tech/GitLab.png",
    position: "right-[14%] top-[4%]",
    size: "size-8 lg:size-9",
    duration: "7.1s",
    delay: "0.9s",
  },
  {
    name: "Flask",
    src: "/tech/Flask.png",
    position: "left-[4%] top-[6%]",
    size: "size-7 lg:size-8",
    duration: "5.7s",
    delay: "1.0s",
  },
  {
    name: "Spring",
    src: "/tech/Spring.png",
    position: "right-[4%] top-[6%]",
    size: "size-7 lg:size-8",
    duration: "6.1s",
    delay: "1.4s",
  },

  // -- Left constellation --
  {
    name: "PostgreSQL",
    src: "/tech/postgresql.png",
    position: "left-[6%] top-[18%]",
    size: "size-10 lg:size-11",
    duration: "7.2s",
    delay: "0.3s",
  },
  {
    name: "Python",
    src: "/tech/python.png",
    position: "left-[18%] top-[14%]",
    size: "size-9 lg:size-10",
    duration: "6.9s",
    delay: "1.5s",
  },
  {
    name: "MySQL",
    src: "/tech/MySQL.png",
    position: "left-[3%] top-[30%]",
    size: "size-8 lg:size-9",
    duration: "6.6s",
    delay: "0.65s",
  },
  {
    name: "React",
    src: "/tech/react.png",
    position: "left-[12%] top-[34%]",
    size: "size-14 lg:size-[4.25rem]",
    duration: "6.4s",
    delay: "0s",
  },
  {
    name: "Git",
    src: "/tech/Git.png",
    position: "left-[2%] top-[44%]",
    size: "size-7 lg:size-8",
    duration: "6.5s",
    delay: "0.55s",
  },
  {
    name: "Firebase",
    src: "/tech/Firebase.png",
    position: "left-[18%] top-[48%]",
    size: "size-8 lg:size-9",
    duration: "7.5s",
    delay: "1.7s",
  },
  {
    name: "Node.js",
    src: "/tech/Node.png",
    position: "left-[5%] top-[56%]",
    size: "size-10 lg:size-11",
    duration: "7.8s",
    delay: "1.1s",
  },
  {
    name: "RabbitMQ",
    src: "/tech/RabbitMQ.png",
    position: "left-[17%] top-[62%]",
    size: "size-8 lg:size-9",
    duration: "6.2s",
    delay: "2.1s",
  },
  {
    name: "Docker",
    src: "/tech/docker.png",
    position: "left-[8%] top-[70%]",
    size: "size-12 lg:size-14",
    duration: "5.8s",
    delay: "0.7s",
  },
  {
    name: "Java",
    src: "/tech/Java.png",
    position: "left-[3%] top-[82%]",
    size: "size-9 lg:size-10",
    duration: "7.7s",
    delay: "0.1s",
  },
  {
    name: "Express",
    src: "/tech/express.png",
    position: "left-[14%] bottom-[5%]",
    size: "size-8 lg:size-9",
    duration: "7.1s",
    delay: "1.9s",
  },
  {
    name: "Jenkins",
    src: "/tech/Jenkins.png",
    position: "left-[24%] bottom-[3%]",
    size: "size-9 lg:size-10",
    duration: "7.3s",
    delay: "1.8s",
  },
  {
    name: "Digital Ocean",
    src: "/tech/Digital%20Ocean.png",
    position: "left-[8%] bottom-[2%]",
    size: "size-8 lg:size-9",
    duration: "6.0s",
    delay: "0.85s",
  },

  // -- Right constellation --
  {
    name: "MongoDB",
    src: "/tech/mongodb.png",
    position: "right-[18%] top-[14%]",
    size: "size-9 lg:size-10",
    duration: "6.2s",
    delay: "0.9s",
  },
  {
    name: "Kubernetes",
    src: "/tech/kubernetes.png",
    position: "right-[7%] top-[18%]",
    size: "size-12 lg:size-[4.25rem]",
    duration: "7.6s",
    delay: "0.4s",
  },
  {
    name: "Azure",
    src: "/tech/Azure.png",
    position: "right-[3%] top-[30%]",
    size: "size-8 lg:size-9",
    duration: "6.7s",
    delay: "1.25s",
  },
  {
    name: "PHP",
    src: "/tech/PHP.png",
    position: "right-[16%] top-[34%]",
    size: "size-8 lg:size-9",
    duration: "8.2s",
    delay: "2s",
  },
  {
    name: "Redis",
    src: "/tech/redis.png",
    position: "right-[5%] top-[44%]",
    size: "size-10 lg:size-11",
    duration: "5.9s",
    delay: "1.7s",
  },
  {
    name: "Google Cloud",
    src: "/tech/Google%20Cloud.png",
    position: "right-[18%] top-[48%]",
    size: "size-8 lg:size-9",
    duration: "7.0s",
    delay: "0.45s",
  },
  {
    name: "TensorFlow",
    src: "/tech/tensorflow.png",
    position: "right-[4%] top-[56%]",
    size: "size-8 lg:size-9",
    duration: "8s",
    delay: "1.2s",
  },
  {
    name: "scikit-learn",
    src: "/tech/scikit-learn.png",
    position: "right-[16%] top-[60%]",
    size: "size-8 lg:size-9",
    duration: "6.5s",
    delay: "2.2s",
  },
  {
    name: "FastAPI",
    src: "/tech/fastapi.webp",
    position: "right-[8%] top-[70%]",
    size: "size-11 lg:size-[3.75rem]",
    duration: "6.6s",
    delay: "0.6s",
  },
  {
    name: "AWS",
    src: "/tech/AWS.png",
    position: "right-[3%] top-[82%]",
    size: "size-9 lg:size-10",
    duration: "6.8s",
    delay: "0.5s",
  },
  {
    name: "Linux",
    src: "/tech/Linux.png",
    position: "right-[14%] bottom-[5%]",
    size: "size-9 lg:size-10",
    duration: "8.4s",
    delay: "0.35s",
  },
];

const stackGroups: StackGroup[] = [
  {
    label: "Frontend",
    blurb: "Interfaces that feel native and stay fast.",
    items: ["React", "JavaScript", "Tailwind", "Vite", "Flutter", "VS Code"],
    icons: [
      "/tech/react.png",
      "/tech/javascript.png",
      "/tech/Tailwind%20CSS.png",
      "/tech/Vite.png",
      "/tech/Flutter.png",
    ],
  },
  {
    label: "Backend",
    blurb: "APIs and services built for real traffic.",
    items: [
      "Node.js",
      "Python",
      "Java",
      "FastAPI",
      "Spring",
      "PHP",
      "Flask",
      "Express",
    ],
    icons: [
      "/tech/Node.png",
      "/tech/python.png",
      "/tech/Java.png",
      "/tech/fastapi.webp",
      "/tech/Spring.png",
    ],
  },
  {
    label: "Data & AI",
    blurb: "Reliable stores, queues, and learning systems.",
    items: [
      "PostgreSQL",
      "MongoDB",
      "Redis",
      "MySQL",
      "RabbitMQ",
      "TensorFlow",
      "scikit-learn",
    ],
    icons: [
      "/tech/postgresql.png",
      "/tech/mongodb.png",
      "/tech/redis.png",
      "/tech/MySQL.png",
      "/tech/tensorflow.png",
    ],
  },
  {
    label: "Cloud & DevOps",
    blurb: "Shipped on containers, orchestrated at scale.",
    items: [
      "AWS",
      "Azure",
      "Google Cloud",
      "Digital Ocean",
      "Firebase",
      "Docker",
      "Kubernetes",
      "Jenkins",
      "Linux",
      "Git",
      "GitHub",
      "GitLab",
    ],
    icons: [
      "/tech/AWS.png",
      "/tech/Azure.png",
      "/tech/Google%20Cloud.png",
      "/tech/docker.png",
      "/tech/kubernetes.png",
    ],
  },
];

function TechTile({
  src,
  name,
  duration,
  delay,
}: {
  src: string;
  name: string;
  duration: string;
  delay: string;
}) {
  return (
    <div className="flex shrink-0 items-center justify-center rounded-2xl border border-black/[0.06] bg-white p-3.5 shadow-[0_8px_24px_rgba(0,0,0,0.05)] dark:border-white/[0.08] dark:bg-[#161616]">
      <img
        src={src}
        alt={name}
        className="size-9 object-contain sm:size-10"
        style={{
          animation: `tech-float ${duration} ease-in-out infinite`,
          animationDelay: delay,
        }}
        loading="lazy"
        decoding="async"
      />
    </div>
  );
}

export function Technologies() {
  const navigate = useNavigate();
  const lang = useSiteLanguage();
  const t = techCopy[lang];
  const tx = techExtraCopy[lang];
  const tg = techStackGroupCopy[lang];
  const marquee = [...techIcons, ...techIcons];
  const localizedGroups = stackGroups.map((group, i) => ({
    ...group,
    label: tg.labels[i] ?? group.label,
    blurb: tg.blurbs[i] ?? group.blurb,
  }));

  return (
    <section
      id="technologies"
      className="relative overflow-hidden bg-background py-14 sm:py-16 md:py-20 lg:py-24"
      aria-labelledby="technologies-heading"
    >
      <div className="relative mx-auto w-full max-w-[1400px] px-5 sm:px-6 lg:px-10">
        {/* Mobile / tablet */}
        <div className="lg:hidden">
          <div className="max-w-3xl">
            <p className="mb-3 text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
              {t.eyebrow}
            </p>
            <h2
              id="technologies-heading"
              className="text-balance text-4xl font-bold tracking-tight sm:text-5xl"
            >
              {t.title}
            </h2>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
              {t.subtitle}
            </p>
            <Button
              size="lg"
              className="mt-7 rounded-xl px-7 py-5 text-base"
              onClick={() => navigate("/work")}
            >
              {t.cta}
            </Button>
          </div>

          <div className="relative mt-10 -mx-5 sm:-mx-6">
            <div
              className="pointer-events-none absolute inset-y-0 left-0 z-10 w-8 bg-gradient-to-r from-background to-transparent sm:w-10"
              aria-hidden
            />
            <div
              className="pointer-events-none absolute inset-y-0 right-0 z-10 w-8 bg-gradient-to-l from-background to-transparent sm:w-10"
              aria-hidden
            />
            <div className="work-marquee-track overflow-hidden py-1">
              <div
                className="animate-work-marquee flex w-max gap-3 px-5 sm:gap-4 sm:px-6"
                style={{ animationDuration: "52s" }}
              >
                {marquee.map((icon, i) => (
                  <TechTile
                    key={`${icon.name}-${i}`}
                    src={icon.src}
                    name={icon.name}
                    duration={icon.duration}
                    delay={icon.delay}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="mt-10 grid gap-3 sm:grid-cols-2 sm:gap-4">
            {localizedGroups.map((group) => (
              <article
                key={group.label}
                className="rounded-[1.35rem] border border-black/[0.06] bg-[#F7F7F8] p-4 dark:border-white/[0.08] dark:bg-[#141414] sm:p-5"
              >
                <div className="mb-3 flex items-center gap-2">
                  {group.icons.slice(0, 3).map((src) => (
                    <span
                      key={src}
                      className="flex size-8 items-center justify-center rounded-lg border border-black/[0.05] bg-white dark:border-white/[0.08] dark:bg-[#1c1c1c]"
                    >
                      <img
                        src={src}
                        alt=""
                        className="h-4 w-4 object-contain"
                        loading="lazy"
                      />
                    </span>
                  ))}
                </div>
                <h3 className="text-[15px] font-semibold tracking-tight text-foreground">
                  {group.label}
                </h3>
                <p className="mt-1 text-[13px] leading-snug text-muted-foreground">
                  {group.blurb}
                </p>
                <ul className="mt-3 flex flex-wrap gap-1.5">
                  {group.items.map((item) => (
                    <li
                      key={item}
                      className="rounded-md border border-border/70 bg-background/80 px-2 py-0.5 text-[11px] font-medium text-foreground/80"
                    >
                      {item}
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>

        {/* Desktop: floating constellation around centered copy */}
        <div className="relative mx-auto hidden min-h-[38rem] w-full lg:block lg:min-h-[42rem]">
          {techIcons.map((icon) => (
            <div
              key={icon.name}
              className={cn("pointer-events-none absolute z-0", icon.position)}
              aria-hidden
            >
              <img
                src={icon.src}
                alt=""
                title={icon.name}
                className={cn(
                  "object-contain drop-shadow-[0_10px_18px_rgba(0,0,0,0.16)] dark:drop-shadow-[0_10px_18px_rgba(0,0,0,0.45)]",
                  icon.size,
                )}
                style={{
                  animation: `tech-float ${icon.duration} ease-in-out infinite`,
                  animationDelay: icon.delay,
                }}
                loading="lazy"
                decoding="async"
              />
            </div>
          ))}

          <div className="relative z-10 mx-auto flex h-full min-h-[42rem] max-w-xl flex-col items-center justify-center px-6 text-center">
            <p className="mb-3 text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
              {t.eyebrow}
            </p>
            <h2
              id="technologies-heading-desktop"
              className="text-balance text-[3.25rem] font-bold leading-[1.15] tracking-tight text-foreground"
            >
              {t.title}
            </h2>
            <p className="mt-5 max-w-lg text-pretty text-lg leading-relaxed text-muted-foreground">
              {t.subtitleDesktop}
            </p>
            <Button
              size="lg"
              className="mt-8 rounded-xl px-8 py-6 text-base"
              onClick={() => navigate("/work")}
            >
              {t.cta}
            </Button>
          </div>
        </div>

        {/* Desktop stack - premium editorial columns */}
        <div className="relative z-10 mx-auto mt-16 hidden max-w-6xl lg:block">
          <div className="mb-10 flex items-end justify-between gap-8 border-b border-border/50 pb-6">
            <div>
              <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-muted-foreground">
                {tx.byLayer}
              </p>
              <h3 className="mt-2 text-2xl font-semibold tracking-tight text-foreground">
                {tx.stackWork}
              </h3>
            </div>
            <p className="hidden max-w-sm text-right text-sm leading-relaxed text-muted-foreground xl:block">
              {tx.stackBlurb}
            </p>
          </div>

          <div className="grid grid-cols-4 gap-0">
            {localizedGroups.map((group, index) => (
              <article
                key={group.label}
                className={cn(
                  "group relative px-6 py-2 first:pl-0 last:pr-0",
                  index > 0 &&
                    "border-l border-border/50 pl-7",
                )}
              >
                <div className="mb-5 flex items-center justify-between">
                  <span className="font-mono text-[11px] tracking-wider text-muted-foreground/80">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <div className="flex items-center -space-x-2.5">
                    {group.icons.slice(0, 4).map((src, i) => (
                      <span
                        key={src}
                        className="relative flex size-9 items-center justify-center rounded-full border border-border/60 bg-background shadow-[0_4px_12px_rgba(0,0,0,0.06)] transition-transform duration-300 group-hover:-translate-y-0.5 dark:bg-[#161616] dark:shadow-[0_4px_12px_rgba(0,0,0,0.35)]"
                        style={{ zIndex: 4 - i, transitionDelay: `${i * 40}ms` }}
                      >
                        <img
                          src={src}
                          alt=""
                          className="h-[18px] w-[18px] object-contain"
                          loading="lazy"
                        />
                      </span>
                    ))}
                  </div>
                </div>

                <h4 className="text-lg font-semibold tracking-tight text-foreground">
                  {group.label}
                </h4>
                <p className="mt-2 min-h-[2.75rem] text-[13px] leading-relaxed text-muted-foreground">
                  {group.blurb}
                </p>

                <ul className="mt-6 space-y-2.5 border-t border-border/40 pt-5">
                  {group.items.map((item) => (
                    <li
                      key={item}
                      className="flex items-center gap-2.5 text-[13px] font-medium text-foreground/85"
                    >
                      <span
                        className="h-1 w-1 shrink-0 rounded-full bg-foreground/35"
                        aria-hidden
                      />
                      {item}
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
